import type { StockReportOutcome, StockReportRow } from "../api/rest";
import { buildXlsx, type XlsxColumn, type XlsxValue } from "./xlsx";

/**
 * The customer-facing stock report as a workbook (ADR 241, ADR 264).
 *
 * Shaped to the sheet the customer already reads — same eight columns, same
 * Portuguese status words, same one-row-per-address order — so it can be laid
 * next to their own file and compared line by line without anyone re-mapping
 * vocabulary in their head. The classification itself is the backend's
 * (StockReportService); nothing here decides an outcome.
 */

/** Status vocabulary, verbatim from the customer's sheet — including the
 *  unaccented "Fisico", which is theirs, not a typo of ours. Their legend, and
 *  what each maps to on our side:
 *
 *  - Ok                     — o sistema está igual ao físico
 *  - Encontrado             — não tem saldo, mas tem palete no local
 *  - Fisico (Não escaneado) — tem saldo, tem físico, mas a caixa não escaneou
 *  - Fisico não confere     — tem saldo, escaneou, mas o físico é diferente
 *
 *  The last one is reached through a confirmed-empty reading rather than a
 *  quantity comparison: an iC3 product export carries no quantities, but an
 *  address the camera visited and read empty is "o físico é diferente" in its
 *  strongest form — the physical count is zero.
 */
export const FLEX_STATUS: Record<StockReportOutcome, string> = {
  OK: "Ok",
  FOUND: "Encontrado",
  NOT_SCANNED: "Fisico (Não escaneado)",
  MISMATCH: "Fisico não confere",
};

/**
 * The sheet's columns, and their types.
 *
 * Typing them is the whole reason this is a workbook and not the CSV it replaced.
 * A CSV cell is text, so FIFO had to be written "23/06/2026" to look right in a
 * pt-BR locale — and then sorted alphabetically, which on a report about aging is
 * worse than no column at all. As a date serial it sorts chronologically and
 * filters by month; as numbers, Sistema and Contado total in the status bar.
 */
export const FLEX_COLUMNS: XlsxColumn[] = [
  { header: "Warehouse",    width: 14, type: "text" },
  { header: "CódigodoItem", width: 20, type: "text" },
  { header: "Description",  width: 42, type: "text" },
  { header: "FIFO",         width: 12, type: "date" },
  { header: "Location",     width: 14, type: "text" },
  { header: "Sistema",      width: 11, type: "number" },
  { header: "Contado",      width: 11, type: "number" },
  { header: "Status",       width: 24, type: "text" },
];

/** `YYYY-MM-DD` → a Date whose UTC fields are that calendar date, which is what
 *  `excelSerial` reads. A malformed value becomes null rather than an invented
 *  date: the FIFO column is enrichment and may legitimately be absent. */
export function isoToUtcDate(iso: string | null): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.slice(0, 10));
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : null;
}

/**
 * Project report rows onto the sheet's columns.
 *
 * `Contado` distinguishes two things the customer's own sheet cannot: blank means
 * nobody reached the address, `0` means it was read and held nothing. Collapsing
 * them would throw away the more actionable of the two findings.
 *
 * `Sistema` renders a missing WMS balance as `0`, not blank — we hold no line for
 * that address, and "não tem saldo" is exactly what zero says. Their sheet prints
 * 0 on those rows too.
 */
export function flexReportRows(rows: StockReportRow[], warehouse: string): XlsxValue[][] {
  return rows.map((r) => [
    warehouse,
    r.product ?? "",
    r.description ?? "",
    isoToUtcDate(r.fifoDate),
    r.address,
    r.expectedQty ?? 0,
    r.countedQty ?? null,
    FLEX_STATUS[r.outcome] ?? r.outcome,
  ]);
}

export function flexReportToXlsx(rows: StockReportRow[], warehouse: string): Uint8Array {
  return buildXlsx({
    name: "Relatório",
    columns: FLEX_COLUMNS,
    rows: flexReportRows(rows, warehouse),
  });
}
